/**
 * The bold landing page (option D of the Innovation Days design): the limits
 * both the admin controls and the participant pages share, and the two small
 * pieces of logic they must agree on. The limits mirror firestore.rules and
 * storage.rules; the rules are the authority and a change there is a change
 * here.
 */

export const HERO_KICKER_MAX = 80;
export const HERO_HEADLINE_MAX = 60;
export const HERO_SUBLINE_MAX = 160;
export const HERO_STAT_VALUE_MAX = 8;
export const HERO_STAT_LABEL_MAX = 30;
export const STAT_SLOTS = ["a", "b", "c"] as const;
export type StatSlot = (typeof STAT_SLOTS)[number];

export const DAY_TITLE_MAX = 80;
export const DAY_TITLES_MAX = 14;

export const HIGHLIGHT_TITLE_MAX = 40;
export const HIGHLIGHT_BLURB_MAX = 300;

/** The Storage rule's cap. The admin guidance asks for about 3 MiB. */
export const HERO_VIDEO_MAX_BYTES = 10 * 1024 * 1024;
export const HERO_VIDEO_TYPE = "video/mp4";
/** Not enforceable by rules: the browser checks both before the upload. */
export const HERO_VIDEO_MAX_SECONDS = 15;
export const HERO_VIDEO_MAX_WIDTH = 1280;

/** One fixed object per event, so a replacement overwrites the old file. */
export const heroVideoPath = (eventId: string): string =>
  `events/${eventId}/hero-video/hero-video`;

const HEADLINE_LINES = 3;

export type SplitHeadline = {
  lines: string[];
  /** Index of the line drawn in the accent, or -1 when there are no lines. */
  accentIndex: number;
};

/**
 * The headline as up to three lines, split on line breaks. The last line is
 * the accent ("Ship" / "it."), so a single line is its own accent.
 */
export function splitHeadline(
  headline: string | null | undefined,
): SplitHeadline {
  const all = (headline ?? "")
    .split(/\r\n|[\r\n\u2028\u2029]/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  // The rules bound the length, not the number of lines, so a short headline
  // can have more than three. The extra lines fold into the third: the last
  // line is the accent, and it must be the real last line.
  const lines =
    all.length > HEADLINE_LINES
      ? [
          ...all.slice(0, HEADLINE_LINES - 1),
          all.slice(HEADLINE_LINES - 1).join(" "),
        ]
      : all;
  return { lines, accentIndex: lines.length - 1 };
}

/**
 * Which of the three chapter styles a chapter takes: its place among the
 * highlights, 1 to 3. Neutral on purpose, because what each slot looks like
 * differs by theme and is decided where it is drawn (ChapterBlocks).
 */
export const CHAPTER_SLOTS = [1, 2, 3] as const;
export type ChapterSlot = (typeof CHAPTER_SLOTS)[number];
/** Only the first three highlighted sessions become chapters. */
export const CHAPTER_LIMIT = CHAPTER_SLOTS.length;

export type HighlightSource = {
  id: string;
  title: string;
  startsAt: Date;
  description?: string | null;
  highlight?: boolean | null;
  highlightTitle?: string | null;
  highlightBlurb?: string | null;
};

export type Chapter = {
  id: string;
  /** "01", "02", "03" in time order. */
  number: string;
  slot: ChapterSlot;
  title: string;
  /** The card text; empty when the session has neither a blurb nor a description. */
  blurb: string;
  startsAt: Date;
};

function shorten(text: string, limit: number): string {
  // By code point, so an emoji is never cut between its two halves.
  const flat = Array.from(text.replace(/\s+/g, " ").trim());
  if (flat.length <= limit) return flat.join("");
  const cut = flat.slice(0, limit - 1).join("");
  const lastSpace = cut.lastIndexOf(" ");
  const base = lastSpace > limit / 2 ? cut.slice(0, lastSpace) : cut;
  return `${base.trimEnd()}…`;
}

/**
 * The sessions marked as highlights, in time order, as chapters. The number
 * and the slot come from the position, so reordering the agenda renumbers
 * them. Title and blurb fall back to the session's own.
 */
export function highlightChapters(
  items: readonly HighlightSource[],
): Chapter[] {
  return items
    .filter((item) => item.highlight === true)
    .sort(
      (a, b) =>
        a.startsAt.getTime() - b.startsAt.getTime() || a.id.localeCompare(b.id),
    )
    .slice(0, CHAPTER_LIMIT)
    .map((item, index) => ({
      id: item.id,
      number: String(index + 1).padStart(2, "0"),
      slot: CHAPTER_SLOTS[index],
      title: item.highlightTitle?.trim() || item.title,
      blurb:
        item.highlightBlurb?.trim() ||
        shorten(item.description ?? "", HIGHLIGHT_BLURB_MAX),
      startsAt: item.startsAt,
    }));
}
